import { create } from "zustand";

/**
 * The open post composer's draft, for the hosts that can tear it down (the street-chat sheet's outside tap,
 * a panel tab switch, Escape): a dirty draft (changed text or items, uploads in flight) asks before it is
 * thrown away; a clean one closes freely. One composer at a time; the composer owns the confirm dialog.
 */
interface ComposerDraftState {
  /** A composer is mounted inside the wall panel (not as a modal). */
  inPanel: boolean;
  dirty: boolean;
  /** What runs once the user confirms the discard; non-null while the confirm dialog shows. */
  pending: (() => void) | null;
  /**
   * The open composer's own close request (asks first on a dirty draft). A host whose layer swallows the key,
   * like the narrow-screen sheet's Escape (Radix prevents it before any window listener), calls this.
   */
  requestClose: (() => void) | null;
  set(p: Partial<Pick<ComposerDraftState, "inPanel" | "dirty" | "requestClose">>): void;
  /** Runs `action` now on a clean draft; on a dirty one asks first. */
  guard(action: () => void): void;
  confirmDiscard(): void;
  cancelDiscard(): void;
  reset(): void;
}

export const useComposerDraftStore = create<ComposerDraftState>((set, get) => ({
  inPanel: false,
  dirty: false,
  pending: null,
  requestClose: null,
  set: (p) => set(p),
  guard: (action) => {
    if (!get().dirty) action();
    else set({ pending: action });
  },
  confirmDiscard: () => {
    const action = get().pending;
    // Clean first: the action may close the composer, or ask again through guard().
    set({ pending: null, dirty: false });
    action?.();
  },
  cancelDiscard: () => set({ pending: null }),
  reset: () => set({ inPanel: false, dirty: false, pending: null, requestClose: null }),
}));

/** Host-side: run `action` unless a dirty draft is open, in which case the composer asks first. */
export const guardComposerDraft = (action: () => void): void => useComposerDraftStore.getState().guard(action);
